import { createContext, useReducer, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { getPublicIp } from '../../utils/publicIp';
import api, { refreshAuthToken, setAuthRefreshHandler } from '../../api';
import { useEventListener } from 'use-event-listeners';
import { useDispatch } from 'react-redux';
import { logoutUser, setRefreshToken, setToken, setUser } from '../../store/auth';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { refreshSession } from '../../utils/api/user';
import { batch } from 'react-redux';
import { decodeJwt } from '../../utils/file';
import { useRootMemoSelector } from 'use-redux-states';

const initialState = {
  signOutInterval: null,
  inactiveTimer: 0,
  interval: null,
  userdetails: null,
  refreshToken: null // <-- add this
};

const AuthContext = createContext<{
  authDispatch: React.ActionDispatch<[action: any]>;
  authState: typeof initialState;
}>({ authState: initialState, authDispatch: () => null });
const AuthConsumer = AuthContext.Consumer;

const reducer = () => (state, action) => {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        userdetails: action.payload.user,
        refreshToken: action.payload.refreshToken // <-- store refreshToken
      };
    case 'LOGOUT':
      clearInterval(state.interval);
      return { ...state, refreshToken: null };
    case 'SESSION_TIMEOUT':
      clearInterval(state.interval);
      return { ...state, signOutInterval: action.payload, inactiveTimer: action.payload };
    default:
      return state;
  }
};

const source_url = window.location.href;

const events = ['mousemove', 'mousedown', 'click', 'scroll', 'keypress'];
const REFRESH_LEAD_TIME_MS = 120_000;

const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [authState, authDispatch] = useReducer(reducer(), initialState);
  const [{ ip }, setState] = useState({
    ip: undefined
  });
  const confirmRef = useRef<{ handleOpen?: () => void; handleClose?: () => void } | null>(null);
  const refreshOperationRef = useRef<() => Promise<string | null>>(async () => null);
  const token = useRootMemoSelector('auth.token');
  const refreshToken = useRootMemoSelector('auth.refreshToken');

  useEventListener({
    listeners: { 'log-out': () => authDispatch({ type: 'LOGOUT' }) }
  });

  useEffect(() => {
    getPublicIp()
      .then((data) => {
        const { ip } = data.data;

        setState({
          ip
        });
      })
      .catch((error) => console.log('error => ', error));
  }, []);

  const handleInactivity = () => {
    if (!authState.signOutInterval) return;

    return (authState.interval = setInterval(() => {
      authState.inactiveTimer -= 1000;

      if (authState.inactiveTimer === 60000) {
        handlelogoutConfirmation();
      } else if (authState.inactiveTimer <= 0) {
        toast.info('Session expired', { autoClose: 2000 });
        logoutAction();
      }
    }, 1000));
  };

  const handlelogoutConfirmation = () => confirmRef.current?.handleOpen?.();

  const performTokenRefresh = async () => {
    try {
      if (!refreshToken) {
        return null;
      }

      const { data } = await refreshSession(refreshToken);

      if (data?.token && data?.refreshToken) {
        batch(() => {
          dispatch(setToken(data.token));
          dispatch(setRefreshToken(data.refreshToken));
          // Do not replace a working profile with an incomplete API response.
          if (data.user?.user?.id) dispatch(setUser(data.user));
        });
        authDispatch({
          type: 'LOGIN',
          payload: {
            user: data.user,
            refreshToken: data.refreshToken
          }
        });
        return data.token;
      } else {
        return null;
      }
    } catch {
      return null;
    }
  };

  refreshOperationRef.current = performTokenRefresh;

  const handleRefreshToken = async () => Boolean(await refreshAuthToken());

  // Install before child effects can request recovery of a persisted profile.
  useLayoutEffect(() => {
    setAuthRefreshHandler(() => refreshOperationRef.current());
    return () => setAuthRefreshHandler(null);
  }, []);

  const handleRejectLogoutAction = async () => {
    try {
      clearInterval(authState.interval);
      handleInactivity();
      await handleRefreshToken();
      return;
    } catch (error) {
      toast.info('error refreshing the page', { autoClose: 2000 });
      return;
    }
  };

  events.forEach((item) => {
    window.addEventListener(item, () => {
      authState.inactiveTimer = authState.signOutInterval;
    });
  });

  const logoutAction = async (notifyServer = true) => {
    try {
      events.forEach((item) => {
        window.removeEventListener(item, () => {
          authState.inactiveTimer = authState.signOutInterval;
        });
      });
      clearInterval(authState.interval);

      confirmRef.current?.handleClose?.();

      if (notifyServer && authState.userdetails) {
        try {
          await api.post('/logout', { source_url, ip, user: authState.userdetails });
        } catch (error) {
          console.log(error);
        }
      }

      dispatch(logoutUser());

      window.location.href = '/auth/login';
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message || 'An error occurred', {
        autoClose: 2000
      });
    }
  };

  useEffect(() => {
    if (!authState.signOutInterval) {
      return;
    } else if (window.location.pathname !== '/auth/login') {
      const interval = handleInactivity();

      return () => clearInterval(interval);
    } else {
      clearInterval(authState.interval);
    }
  }, [authState.signOutInterval]);

  useEffect(() => {
    if (window.location.pathname === '/auth/login') return;
    if (!token || !refreshToken) return;

    let refreshTimer: ReturnType<typeof setTimeout>;
    let expiryTimer: ReturnType<typeof setTimeout>;
    let refreshPromise: Promise<boolean> | null = null;
    let refreshSucceeded = false;
    const expirationTime = decodeJwt(token)?.exp * 1000;
    if (!expirationTime) return;

    const scheduleRefresh = () => {
      clearTimeout(refreshTimer);
      const refreshAt = expirationTime - REFRESH_LEAD_TIME_MS;
      const delay = refreshAt - Date.now();

      if (delay > 0) {
        refreshTimer = setTimeout(runRefresh, delay);
      } else {
        runRefresh();
      }
    };

    const runRefresh = () => {
      if (!refreshPromise) {
        refreshPromise = handleRefreshToken()
          .then((refreshed) => {
            refreshSucceeded = refreshed;
            return refreshed;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }
      return refreshPromise;
    };

    const handleExpiration = async () => {
      const refreshed = refreshSucceeded || (refreshPromise ? await refreshPromise : false);
      if (!refreshed && Date.now() >= expirationTime) {
        toast.info('Session expired', { autoClose: 2000 });
        logoutAction(false);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') scheduleRefresh();
    };

    scheduleRefresh();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    expiryTimer = setTimeout(() => {
      if (Date.now() >= expirationTime) {
        handleExpiration();
      }
    }, Math.max(0, expirationTime - Date.now()));

    return () => {
      clearTimeout(refreshTimer);
      clearTimeout(expiryTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, refreshToken, window.location.pathname]);

  return (
    <AuthContext.Provider value={{ authState, authDispatch }}>
      <ConfirmDialog
        ref={confirmRef}
        handleYes={handleRejectLogoutAction}
        handleLogout={logoutAction}
        content={
          <div className='text-center'>
            <div>You are about to be signed out due to inactivity</div>
            <div>
              Do you want to <strong>stay signed in</strong>?
            </div>
          </div>
        }
      />
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider, AuthConsumer };
